// @ts-nocheck
/**
 * New Remark Status — alnum+space regex on remark input, STATUS dropdown.
 */
import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { friendlyApiError } from '@/utilities/apiErrorMessage';
import { postApiCall } from '@/service/httpCall';
import { ROUTES, STATUS } from '@/utilities/constant';
import { useAuth, errorCheck } from '@/views/dashboard/auth/auth-utils';
import { API_REMARK_STATUS_CREATE } from '@/service/url';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { ScrollPage } from '@/components/layout/PageContainer';
import { PageHeader } from '@/components/feedback/PageHeader';
import { ArrowLeft, Loader2, Save } from 'lucide-react';
import { Link } from 'react-router-dom';

const RemarkStatusCreate = () => {
  const navigate = useNavigate();
  const { checkAuth } = useAuth();
  const [remark, setRemarks] = useState('');
  const [status, setStatus] = useState('');
  const [showToast, setShowToast] = useState(false);
  const [toastProps, setToastProps] = useState<any>(null);
  const [buttonDisable, setButtonDisable] = useState(false);

  useEffect(() => {
    checkAuth(setToastProps, setShowToast);
  }, []);

  function validateForm() {
    let valid = true;
    if (!remark.trim()) {
      valid = false;
      toast.warning('Remark is required');
    }
    if (!status) {
      valid = false;
      toast.warning('Status is required');
    }
    return valid;
  }

  async function submitForm() {
    setButtonDisable(true);
    if (!validateForm()) {
      setButtonDisable(false);
      return;
    }
    const params = { remark, status: Number(status) };
    postApiCall(API_REMARK_STATUS_CREATE, params)
      .then(() => {
        toast.success('Remark Status created successfully');
        setTimeout(() => {
          navigate(`/${ROUTES.REMARKS.LIST}`);
        }, 1500);
      })
      .catch((err: any) => {
        if (typeof err !== 'object' && err.includes('TokenExpiredError')) {
          errorCheck(err, navigate, setToastProps, setShowToast);
        } else {
          setButtonDisable(false);
          toast.error(friendlyApiError(err) || 'Failed to create remark status');
        }
      });
  }

  return (
    <ScrollPage>
      <PageHeader
        title="New Remark Status"
        description="Add a remark tag agents can pick from during dispositions."
        actions={
          <Button asChild variant="outline">
            <Link to={`/${ROUTES.REMARKS.LIST}`}>
              <ArrowLeft className="h-4 w-4" />
              Back
            </Link>
          </Button>
        }
      />
      <div
        className="rounded-2xl border border-border/60 bg-card p-5"
        style={{
          boxShadow:
            '6px 6px 14px rgba(163, 177, 198, 0.35), -6px -6px 14px rgba(255, 255, 255, 0.9)',
        }}
      >
        <form>
          <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
            <div className="space-y-1.5">
              <label className="text-xs font-semibold text-foreground">
                Remark <span className="text-destructive">*</span>
              </label>
              <input
                type="text"
                id="remark"
                placeholder="Remark name"
                maxLength={100}
                value={remark}
                onChange={(e) => {
                  const value = e.target.value.replace(/[^a-zA-Z0-9 ]/g, '');
                  setRemarks(value);
                }}
                required
                className="input-embossed h-10 w-full rounded-md px-3 text-sm"
              />
            </div>

            <div className="space-y-1.5">
              <label className="text-xs font-semibold text-foreground">
                Status <span className="text-destructive">*</span>
              </label>
              <select
                id="status"
                required
                value={status}
                onChange={(e) => setStatus(e.target.value)}
                className="input-embossed h-10 w-full rounded-md px-3 text-sm"
              >
                <option value="">Select status…</option>
                {STATUS.map((val: any, idx: number) => (
                  <option key={idx} value={val.value}>
                    {val.label}
                  </option>
                ))}
              </select>
            </div>
          </div>

          <div className="mt-5 flex items-center justify-end gap-2 border-t border-border/50 pt-4">
            <Button asChild variant="outline">
              <Link to={`/${ROUTES.REMARKS.LIST}`}>Cancel</Link>
            </Button>
            <Button
              onClick={async () => {
                submitForm();
              }}
              type="button"
              disabled={buttonDisable}
              className="gap-1.5"
            >
              {buttonDisable ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <Save className="h-4 w-4" />
              )}
              {buttonDisable ? 'Adding…' : 'Add remark status'}
            </Button>
          </div>
        </form>
      </div>
    </ScrollPage>
  );
};

export default RemarkStatusCreate;
